/**
 * Climate Zone Utilities
 *
 * US Department of Energy IECC Climate Zones with heating/cooling hours
 * Used for HVAC calculations and regional energy cost estimates
 *
 * Data Sources:
 * - Climate zone classifications: IECC 2021 (International Energy Conservation Code)
 * - Climatic data: ASHRAE Standard 169-2021 (Climatic Data for Building Design Standards)
 * - Cooling/heating hours: Estimated from degree day data and typical HVAC load calculations
 *
 * Note: Values represent typical annual cooling and heating hours for each climate zone,
 * derived from industry-standard degree day data and HVAC engineering practices.
 * These are estimates for comparative analysis and regional adjustments.
 */

// US Climate Zones (IECC 2021/ASHRAE Standard 169)
export const US_CLIMATE_ZONES = {
  '1A': {
    name: 'Hot-Humid',
    coolingHours: 2500,
    heatingHours: 500,
    examples: 'Miami FL, Key West FL',
    description: 'Very hot summers, mild winters, high humidity year-round'
  },
  '2A': {
    name: 'Hot-Humid',
    coolingHours: 2100,
    heatingHours: 900,
    examples: 'Houston TX, Orlando FL, Charleston SC',
    description: 'Hot summers, mild winters, high humidity'
  },
  '2B': {
    name: 'Hot-Dry',
    coolingHours: 2000,
    heatingHours: 850,
    examples: 'Phoenix AZ, Tucson AZ',
    description: 'Very hot summers, mild winters, low humidity'
  },
  '3A': {
    name: 'Warm-Humid',
    coolingHours: 1800,
    heatingHours: 1400,
    examples: 'Atlanta GA, Memphis TN, Birmingham AL',
    description: 'Warm summers, cool winters, moderate humidity'
  },
  '3B': {
    name: 'Warm-Dry',
    coolingHours: 1700,
    heatingHours: 1300,
    examples: 'Las Vegas NV, El Paso TX',
    description: 'Hot summers, cool winters, dry climate'
  },
  '3C': {
    name: 'Warm-Marine',
    coolingHours: 800,
    heatingHours: 1500,
    examples: 'San Francisco CA, San Diego CA',
    description: 'Mild summers and winters, marine influence'
  },
  '4A': {
    name: 'Mixed-Humid',
    coolingHours: 1400,
    heatingHours: 2200,
    examples: 'Greensboro NC, Kansas City MO, Louisville KY',
    description: 'Warm summers, cold winters, moderate humidity'
  },
  '4B': {
    name: 'Mixed-Dry',
    coolingHours: 1300,
    heatingHours: 2100,
    examples: 'Albuquerque NM, Salt Lake City UT',
    description: 'Warm summers, cold winters, dry climate'
  },
  '4C': {
    name: 'Mixed-Marine',
    coolingHours: 600,
    heatingHours: 2000,
    examples: 'Seattle WA, Portland OR',
    description: 'Cool summers, mild winters, marine influence'
  },
  '5A': {
    name: 'Cool-Humid',
    coolingHours: 1000,
    heatingHours: 3000,
    examples: 'Chicago IL, Boston MA, New York NY',
    description: 'Warm summers, very cold winters, moderate humidity'
  },
  '5B': {
    name: 'Cool-Dry',
    coolingHours: 900,
    heatingHours: 2900,
    examples: 'Denver CO, Boise ID',
    description: 'Warm summers, very cold winters, dry climate'
  },
  '6A': {
    name: 'Cold-Humid',
    coolingHours: 700,
    heatingHours: 3800,
    examples: 'Minneapolis MN, Burlington VT',
    description: 'Cool summers, extremely cold winters, moderate humidity'
  },
  '6B': {
    name: 'Cold-Dry',
    coolingHours: 600,
    heatingHours: 3700,
    examples: 'Helena MT, Billings MT',
    description: 'Cool summers, extremely cold winters, dry climate'
  },
  '7': {
    name: 'Very Cold',
    coolingHours: 400,
    heatingHours: 4500,
    examples: 'Duluth MN, Fargo ND, International Falls MN',
    description: 'Cool summers, severe winters with extreme cold'
  },
  '8': {
    name: 'Subarctic',
    coolingHours: 200,
    heatingHours: 6000,
    examples: 'Fairbanks AK, Barrow AK',
    description: 'Cool summers, extreme winters with subarctic conditions'
  }
};

// Zip Code Prefix to Climate Zone Mapping
// Maps first 2 digits of zip code to IECC climate zone
export const ZIP_TO_CLIMATE = {
  // Zone 1A - Hot-Humid (South Florida)
  '33': '1A',

  // Zone 2A - Hot-Humid
  '28': '2A', '29': '2A', // South Carolina
  '32': '2A', '34': '2A', // Central/South Florida
  '77': '2A', '78': '2A', // South Texas
  '70': '2A', '71': '2A', // Louisiana
  '39': '2A', // Mississippi
  '36': '2A', // Alabama (southern)

  // Zone 2B - Hot-Dry
  '85': '2B', '86': '2B', // Arizona

  // Zone 3A - Warm-Humid
  '30': '3A', '31': '3A', // Georgia
  '37': '3A', '38': '3A', // Tennessee
  '35': '3A', // Alabama (northern)
  '72': '3A', '73': '3A', // Arkansas
  '75': '3A', '76': '3A', // North Texas

  // Zone 3B - Warm-Dry
  '89': '3B', // Nevada
  '79': '3B', // West Texas
  '88': '3B', // Nevada (rural)

  // Zone 3C - Warm-Marine
  '90': '3C', '91': '3C', '92': '3C', '93': '3C', '94': '3C', // California Coast
  '95': '3C', '96': '3C', // Northern California

  // Zone 4A - Mixed-Humid (DEFAULT ZONE)
  '27': '4A', // North Carolina
  '40': '4A', '41': '4A', '42': '4A', // Kentucky
  '43': '4A', '45': '4A', // Ohio (southern)
  '46': '4A', '47': '4A', // Indiana (southern)
  '64': '4A', '65': '4A', // Missouri
  '66': '4A', '67': '4A', // Kansas
  '24': '4A', '25': '4A', '26': '4A', // Virginia
  '20': '4A', '21': '4A', // Maryland/DC
  '19': '4A', // Pennsylvania (southern)
  '08': '4A', // New Jersey (southern)

  // Zone 4B - Mixed-Dry
  '87': '4B', // New Mexico
  '84': '4B', // Utah (southern)
  '82': '4B', // Wyoming (southern)

  // Zone 4C - Mixed-Marine
  '98': '4C', // Washington (western)
  '97': '4C', // Oregon (western)

  // Zone 5A - Cool-Humid
  '60': '5A', '61': '5A', '62': '5A', // Illinois
  '46': '5A', '47': '5A', // Indiana (northern)
  '48': '5A', '49': '5A', // Michigan
  '53': '5A', '54': '5A', // Wisconsin
  '50': '5A', '51': '5A', '52': '5A', // Iowa
  '68': '5A', '69': '5A', // Nebraska
  '57': '5A', // South Dakota
  '43': '5A', '44': '5A', // Ohio (northern)
  '15': '5A', '16': '5A', // Pennsylvania (northern)
  '14': '5A', // New York (western)
  '12': '5A', '13': '5A', // New York (eastern)
  '10': '5A', '11': '5A', // New York City
  '06': '5A', '07': '5A', // Connecticut
  '01': '5A', // Massachusetts (western)
  '02': '5A', // Massachusetts (Boston)
  '03': '5A', // New Hampshire
  '04': '5A', // Maine (southern)

  // Zone 5B - Cool-Dry
  '80': '5B', '81': '5B', // Colorado
  '83': '5B', // Idaho (southern)
  '84': '5B', // Utah (northern)
  '82': '5B', // Wyoming (northern)

  // Zone 6A - Cold-Humid
  '55': '6A', // Minnesota (southern)
  '04': '6A', // Maine (northern)
  '05': '6A', // Vermont

  // Zone 6B - Cold-Dry
  '59': '6B', // Montana
  '83': '6B', // Idaho (northern)

  // Zone 7 - Very Cold
  '55': '7', // Minnesota (northern)
  '56': '7', // Minnesota (far north)
  '58': '7', // North Dakota

  // Zone 8 - Subarctic
  '99': '8'  // Alaska
};

// Default fallback climate zone (Triad NC)
export const DEFAULT_CLIMATE_ZONE = '4A';

/**
 * Detect climate zone from zip code
 * @param {string} zipCode - US zip code (5 digits)
 * @returns {Object|null} Climate zone data or null if invalid
 */
export const detectClimateFromZip = (zipCode) => {
  if (!zipCode || zipCode.length < 5) {
    return null;
  }

  // Get first 2 digits of zip code
  const zipPrefix = zipCode.substring(0, 2);

  // Look up climate zone (with fallback to default)
  const detectedZone = ZIP_TO_CLIMATE[zipPrefix] || DEFAULT_CLIMATE_ZONE;
  const zoneData = US_CLIMATE_ZONES[detectedZone];

  // Return climate data with confidence level
  return {
    zone: detectedZone,
    zoneName: zoneData.name,
    coolingHours: zoneData.coolingHours,
    heatingHours: zoneData.heatingHours,
    examples: zoneData.examples,
    description: zoneData.description,
    confidence: ZIP_TO_CLIMATE[zipPrefix] ? 'high' : 'fallback'
  };
};

/**
 * Get climate data for a specific zone
 * @param {string} zoneCode - Climate zone code (e.g., '4A')
 * @returns {Object} Climate zone data
 */
export const getClimateData = (zoneCode) => {
  const zone = US_CLIMATE_ZONES[zoneCode] || US_CLIMATE_ZONES[DEFAULT_CLIMATE_ZONE];
  return {
    zone: zoneCode,
    ...zone
  };
};

/**
 * Get all climate zones as options for dropdown
 * @returns {Array} Array of { value, label } objects
 */
export const getClimateZoneOptions = () => {
  return Object.keys(US_CLIMATE_ZONES).map(code => ({
    value: code,
    label: `${code} - ${US_CLIMATE_ZONES[code].name} (${US_CLIMATE_ZONES[code].examples})`,
    name: US_CLIMATE_ZONES[code].name,
    coolingHours: US_CLIMATE_ZONES[code].coolingHours,
    heatingHours: US_CLIMATE_ZONES[code].heatingHours
  }));
};

/**
 * Validate US zip code format
 * @param {string} zipCode - Zip code to validate
 * @returns {boolean} True if valid format
 */
export const isValidZipCode = (zipCode) => {
  if (!zipCode) return false;
  const cleanZip = zipCode.replace(/[^0-9]/g, '');
  return cleanZip.length === 5;
};
